// libs/core/src/jmkw.component.ts

import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import Map from '@arcgis/core/Map';
import {
  ButtonParams,
  EsriViewType,
  ExtendedMap,
  JepLayer,
  LayerResults,
  Plugin,
  PluginConfigurationRecord,
  PluginMap,
  ViewConfig
} from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import { combineLatest, filter, map, take } from 'rxjs';
import { store } from './jmkw.store';
import { JMKW_VERSION } from './lib/version';
import {
  applyButtonConfigurations,
  applyLayerConfigurations,
  applyLegendConfigurations,
  pluginArrayToMap
} from './plugin-utils';

@Component({
  selector: 'jmkw',
  templateUrl: './jmkw.component.html',
  styleUrls: ['./jmkw.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JmkwComponent implements OnInit {
  private readonly _logger = inject(NGXLogger);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _elementRef = inject(ElementRef);
  private _lastAppliedConfigHash?: string;
  private _lastKnownPluginMap?: PluginMap;

  // Computed properties
  readonly buttonMap = computed(
    () => store.pluginMap()?.allButtons() ?? new ExtendedMap<ButtonParams>()
  );

  readonly activeWidgetMap = computed(
    () => store.activeWidgetMap() || new Map()
  );

  readonly hideToolbar = computed(() => store.env.hideToolbar?.() || false);

  readonly hideWidgetBar = computed(() => {
    const widgetMap = this.activeWidgetMap();
    return store.env.hideWidgetBar?.() || !widgetMap?.size;
  });

  // Input setters
  @Input() set plugins(plugins: Plugin[]) {
    if (plugins?.length) {
      this._processAndUpdatePlugins(plugins, 'client Plugins');
    }
  }

  @Input() set pluginMap(pluginMap: PluginMap) {
    if (pluginMap?.size) {
      this._updatePluginMap(pluginMap, 'client pluginMap', pluginMap);
    }
  }

  @Input() set configuration(configuration: PluginConfigurationRecord) {
    if (configuration) {
      store.pluginConfigurations.update(() => configuration);
      // Effect will handle application when appropriate
    }
  }

  @Input() set layers(layers: JepLayer[]) {
    if (layers?.length) {
      const plugins = layers.map(
        (layer) => new Plugin({ id: layer.id, layer })
      );
      this._processAndUpdatePlugins(plugins, 'client Layers');
    }
  }

  // Output events
  @Output() readonly mapLoaded = new EventEmitter<Map>();
  @Output() readonly viewsLoaded = new EventEmitter<
    Record<EsriViewType, ViewConfig>
  >();
  @Output() readonly initialized = new EventEmitter<PluginMap>();
  @Output() readonly layersLoaded = new EventEmitter<LayerResults>();
  @Output() readonly pluginsUpdated = new EventEmitter<PluginMap>();

  constructor() {
    // Apply configurations whenever plugins or configs change (with deduplication)
    effect(() => {
      const pluginMap = store.pluginMap();
      const configurations = store.pluginConfigurations();

      if (pluginMap && configurations && pluginMap.size > 0) {
        const configHash = this._getConfigHash(configurations);
        const configChanged = configHash !== this._lastAppliedConfigHash;

        if (configChanged) {
          // Consumer deliberately updated [configuration] — apply to all plugins
          this._safeExecute(() => {
            this._applyConfigurations(pluginMap, configurations);
            this._lastAppliedConfigHash = configHash;
            this._lastKnownPluginMap = pluginMap;
          }, 'Error applying configurations');
        } else {
          // pluginMap changed — apply config only to new or replaced plugin instances.
          // Compares by object reference so that a replaced plugin (e.g. IFR toggle
          // sending fresh layer instances) gets its config reapplied, while an already-
          // configured plugin whose reference is unchanged is left alone.
          const newOrReplacedConfigs = Object.fromEntries(
            Object.entries(configurations).filter(([id]) =>
              !this._lastKnownPluginMap ||
              this._lastKnownPluginMap.get(id) !== pluginMap.get(id)
            )
          ) as PluginConfigurationRecord;

          if (Object.keys(newOrReplacedConfigs).length > 0) {
            this._safeExecute(() => {
              this._applyConfigurations(pluginMap, newOrReplacedConfigs);
              this._lastKnownPluginMap = pluginMap;
            }, 'Error applying configurations for new plugins');
          }
        }

        this.pluginsUpdated.emit(pluginMap);
      } else if (pluginMap) {
        this.pluginsUpdated.emit(pluginMap);
      }
    });
  }

  ngOnInit(): void {
    this._trackInitialization();
    if (JMKW_VERSION) {
      this._elementRef.nativeElement.style.setProperty(
        '--jmkw-version-label',
        `'@jmkw v${JMKW_VERSION}. '`
      );
    }
  }

  mapLoadedEvent(esriMap: Map): void {
    this.mapLoaded.emit(esriMap);
    this._setupZoomTracking();
  }

  layersLoadedEvent(results: LayerResults): void {
    if (!results) return;

    this.layersLoaded.emit(results);

    // Emit initialized event when all layers are loaded
    const pluginMap = store.pluginMap();
    if (
      pluginMap?.allLayers().size === Object.keys(results).length &&
      !store.state.initialized()
    ) {
      this.initialized.emit(pluginMap);
      store.state.initialized.set(true);
    }
  }

  viewsLoadedEvent(results: Record<EsriViewType, ViewConfig>): void {
    this.viewsLoaded.emit(results);
  }

  private _processAndUpdatePlugins(plugins: Plugin[], source: string): void {
    this._safeExecute(() => {
      const pluginMap = pluginArrayToMap(plugins);
      this._updatePluginMap(pluginMap, source, plugins);
    }, `Error processing ${source.toLowerCase()}`);
  }

  private _setupZoomTracking(): void {
    this.viewsLoaded
      .pipe(
        map((config) => config?.['2D'].view()),
        take(1)
      )
      .subscribe((mapView) => {
        if (mapView) {
          reactiveUtils.watch(
            () => mapView.zoom,
            (newValue: number, oldValue?: number) => {
              if (Number.isInteger(newValue)) {
                const oldZoom = oldValue
                  ? Math.floor(oldValue)
                  : 'NO VALUE SET';
                this._logger.debug(
                  `[JmkwComponent] zoom level changed from ${oldZoom} to ${newValue}`
                );
              }
            }
          );
        }
      });
  }

  private _applyConfigurations(
    pluginMap: PluginMap,
    configs: PluginConfigurationRecord
  ): void {
    if (!pluginMap || !configs) return;

    this._safeExecute(() => {
      Object.entries(configs).forEach(([id, config]) => {
        if (config?.layer) {
          const layer = pluginMap.allLayers().get(id);
          if (layer) applyLayerConfigurations(layer, config.layer);
        }

        if (config?.button) {
          const button = pluginMap.allButtons().get(id);
          if (button) applyButtonConfigurations(button, config.button);
        }

        if (config?.legend) {
          const legend = pluginMap.allLegends().get(id);
          if (legend) applyLegendConfigurations(legend, config.legend);
        }
      });
    }, 'Error applying configurations');
  }

  private _updatePluginMap(
    pluginMap: PluginMap,
    source: string,
    payload: unknown
  ): void {
    if (!pluginMap) return;

    this._safeExecute(() => {
      store.pluginMap.update(
        (current) => (current?.concat(pluginMap) || pluginMap) as PluginMap
      );
      this._logUpdate(`[JmkwComponent] Updated store with ${source}`, payload);
    }, 'Error updating plugin map');
  }

  private _logUpdate(message: string, payload: unknown): void {
    this._safeExecute(() => {
      this._logger.trace(message, store.pluginMap());
    }, 'Error logging update');
  }

  private _getConfigHash(config: PluginConfigurationRecord): string {
    return JSON.stringify(config);
  }

  private _safeExecute(operation: () => void, errorContext: string): void {
    try {
      operation();
    } catch (error) {
      this._logger.error(`[JmkwComponent] ${errorContext}:`, error);
    }
  }

  private _trackInitialization(): void {
    combineLatest([
      this.pluginsUpdated,
      this.mapLoaded,
      this.viewsLoaded,
      this.layersLoaded.pipe(
        filter((results) => {
          const pluginMap = store.pluginMap();
          return pluginMap
            ? pluginMap.allLayers().size === Object.keys(results).length
            : false;
        }),
        take(1)
      )
    ])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(([pluginMap]) => this.initialized.emit(pluginMap));
  }
}
